// macadamia-app/src/macadamia-core/components/postits/PostItManager.tsx
import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PostIt from './PostIt';
import PostItPeek from './PostItPeek';
import { usePostIts } from '../hooks/usePostIts';
import { useAuth } from '../../../context/AuthContext';
import { useDossier } from '../../dossierEditor/DossierContext';

interface PostItManagerProps {
    dossierId: string;
    currentChapterId: string;
    userRole: string | null;
    portalContainer?: React.RefObject<HTMLDivElement>;
}

const PostItManager: React.FC<PostItManagerProps> = ({ dossierId, currentChapterId, userRole, portalContainer }) => {
    const { user } = useAuth();
    const { editMode } = useDossier();
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerBounds, setContainerBounds] = useState<DOMRect | null>(null);
    const [, setDocumentHeight] = useState(document.documentElement.scrollHeight);

    // L'élément scrollable réel (le conteneur de l'éditeur)
    const scrollContainer = portalContainer?.current || null;

    // Hook de gestion des post-its
    const {
        postits,
        createPostIt,
        updatePostIt,
        deletePostIt,
        changeColor,
        changeVisibility,
        refetch
    } = usePostIts({
        dossierId,
        chapterId: currentChapterId,
        userId: user?.id || '',
        containerBounds,
        scrollContainer
    });

    // Re-fetch des post-its quand le chapitre change
    useEffect(() => {
        refetch();
    }, [currentChapterId, refetch]);

    // ========================================
    // MESURE DU CONTAINER
    // ========================================

    // Initialiser avec les dimensions de la fenêtre comme fallback
    useLayoutEffect(() => {
        const updateBounds = () => {
            // Priorité : mesurer le conteneur scrollable (viewport stable) s'il existe
            const targetElement = portalContainer?.current || containerRef.current;

            if (targetElement) {
                const rect = targetElement.getBoundingClientRect();

                // Si on mesure le portalContainer (qui est le scroll parent),
                // ses bounds sont déjà exacts et ne bougent pas avec le scroll.
                setContainerBounds(rect);
            } else {
                // Fallback : utiliser les dimensions de la fenêtre
                const fallbackBounds = {
                    top: 0,
                    left: 0,
                    right: window.innerWidth,
                    bottom: window.innerHeight,
                    width: window.innerWidth,
                    height: window.innerHeight,
                    x: 0,
                    y: 0,
                    toJSON: () => ({})
                } as DOMRect;
                setContainerBounds(fallbackBounds);
            }
        };

        // Appel initial synchrone avec useLayoutEffect
        updateBounds();

        // Re-mesurer lors du resize
        // On observe le portalContainer si possible, car c'est lui qui définit la zone visible
        const elementToObserve = portalContainer?.current || containerRef.current;
        const resizeObserver = new ResizeObserver(updateBounds);

        if (elementToObserve) {
            resizeObserver.observe(elementToObserve);
        }

        window.addEventListener('resize', updateBounds);
        window.addEventListener('scroll', updateBounds, true); // Capture scroll pour re-vérifier si besoin (ex: affichage barres)

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateBounds);
            window.removeEventListener('scroll', updateBounds, true);
        };
    }, [portalContainer]); // Ajout de portalContainer aux dépendances

    // Force une mise à jour après le premier rendu pour s'assurer de la sync
    useEffect(() => {
        const targetElement = portalContainer?.current || containerRef.current;

        if (targetElement && containerBounds) {
            const rect = targetElement.getBoundingClientRect();
            // Vérifier si les bounds ont changé
            if (rect.width !== containerBounds.width || rect.height !== containerBounds.height || rect.top !== containerBounds.top) {
                setContainerBounds(rect);
            }
        }
    }, [containerBounds, portalContainer]);

    // ========================================
    // OBSERVATION DE LA HAUTEUR DU DOCUMENT
    // ========================================

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        const updateDocumentHeight = () => {
            // Utiliser la hauteur du container A4 au lieu de document.documentElement
            // Cela évite que les post-its débordants agrandissent le container (cercle vicieux)
            const a4Container = document.querySelector('.a4-pages-container');
            const newHeight = a4Container
                ? a4Container.scrollHeight
                : document.documentElement.scrollHeight;

            setDocumentHeight(newHeight);
        };

        // Debounced resize handler pour éviter les recalculs frénétiques
        const debouncedUpdateHeight = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(updateDocumentHeight, 150); // 150ms de debounce
        };

        // Mesure initiale
        updateDocumentHeight();

        // Observer les changements de taille du container A4 (ou body en fallback)
        const resizeObserver = new ResizeObserver(debouncedUpdateHeight);
        const a4Container = document.querySelector('.a4-pages-container');
        if (a4Container) {
            resizeObserver.observe(a4Container as Element);
        } else {
            resizeObserver.observe(document.body);
        }

        // Observer aussi les changements de taille de la fenêtre (avec debounce)
        window.addEventListener('resize', debouncedUpdateHeight);

        return () => {
            clearTimeout(timeoutId);
            resizeObserver.disconnect();
            window.removeEventListener('resize', debouncedUpdateHeight);
        };
    }, []);

    // ========================================
    // HANDLERS
    // ========================================

    const handlePeekActivate = useCallback((position?: { x: number; y: number }) => {
        createPostIt(position);
    }, [createPostIt]);

    // ========================================
    // PERMISSIONS ET VIEWPORT
    // ========================================

    const canManagePostIts = userRole === 'author' || userRole === 'admin';
    const isEditorMode = editMode === 'editor';
    const isMobileViewport = window.innerWidth < 768;

    // Ne rien afficher si :
    // - Mode lecteur (reader)
    // - Pas les permissions
    // - Viewport mobile (< 768px) - les post-its ne sont pas adaptés au mobile
    if (!isEditorMode || !canManagePostIts || isMobileViewport) {
        return null;
    }

    // ========================================
    // RENDER
    // ========================================

    // Utiliser un portail React pour isoler les post-its du DragDropContext
    // Rendre dans le portalContainer si fourni, sinon dans document.body
    const portalTarget = portalContainer?.current || document.body;

    const postitsPortal = containerBounds && portalTarget && ReactDOM.createPortal(
        <div
            ref={containerRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%', // height au lieu de minHeight pour ne pas forcer l'expansion
                pointerEvents: 'none', // Container transparent
                zIndex: 29
            }}
        >
            {/* Post-its existants - chaque post-it aura pointerEvents: auto */}
            {postits.map((postit) => (
                <PostIt
                    key={postit._id}
                    postit={postit}
                    onChange={updatePostIt}
                    onDelete={deletePostIt}
                    onColorChange={changeColor}
                    onVisibilityChange={changeVisibility}
                    containerBounds={containerBounds}
                    scrollContainer={scrollContainer}
                />
            ))}
        </div>,
        portalTarget
    );

    return (
        <>
            {postitsPortal}

            {/* Post-it peek */}
            <PostItPeek
                onActivate={handlePeekActivate}
                containerBounds={containerBounds}
                scrollContainer={scrollContainer}
            />
        </>
    );
};

export default PostItManager;